import React, { useEffect, useMemo, useRef, useState } from "react";
import { Send, Plus, Sparkles } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import PulseLoader from "react-spinners/PulseLoader";
import { sendAiChat, confirmAiEdit, cancelAiEdit } from "../../api";
import { useNightMode } from "../../contexts/NightModeContext";
import { useAuth } from "../../context/AuthContext";
import { images } from "../../assets/images";
// Shared page chrome used by every other v3 page (reports, contacts, events)
import PageHeader from "../../components/common/PageHeader/PageHeader";
import Background from "../Login/Background";
import AiEventList, { AiEventItem } from "./AiEventList";
import AiEditProposal, { PendingEditPayload } from "./AiEditProposal";
import { clearAskEvChat, loadAskEvChat, saveAskEvChat } from "./askEvChatStorage";
import { resolveAskEvAccess } from "./askEvAccess";
import "./AiAssistantPage.css";

/** One subtitle for every Ask Ev state, so the page header never shifts. */
const ASK_EV_SUBTITLE =
  "Ask about your events, review drafts, and make quick updates in plain English.";

/** Eventox logo mark — used for the brand row and the assistant avatar. */
const AskEvMark: React.FC<{ size?: "sm" | "md" | "lg" }> = ({ size = "md" }) => {
  const sizeClass =
    size === "lg" ? " ai-ev-mark-lg" : size === "sm" ? " ai-ev-mark-sm" : "";
  return (
    <span className={`ai-ev-mark${sizeClass}`} aria-hidden>
      <img src={images.white_icon} alt="" />
    </span>
  );
};

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  /** Structured events from API — rendered as Eventox event rows */
  events?: AiEventItem[];
  /** Proposed edit waiting for Confirm / Cancel */
  pendingEdit?: PendingEditPayload;
}

function readInitialChat(): {
  messages: ChatMessage[];
  conversationId?: string;
  pendingEdit?: PendingEditPayload;
} {
  const saved = loadAskEvChat();
  if (!saved?.messages?.length) return { messages: [] };
  return {
    messages: saved.messages as ChatMessage[],
    conversationId: saved.conversationId,
    pendingEdit: saved.pendingEdit as PendingEditPayload | undefined,
  };
}

/** Time-aware greeting personalized with the organizer first name. */
function buildTimedGreeting(displayName: string): string {
  const hour = new Date().getHours();
  const name = displayName.trim() || "there";

  const dayKey = "eventox_ai_last_day";
  const sessionKey = "eventox_ai_session";
  const today = new Date().toDateString();
  const lastDay = localStorage.getItem(dayKey);
  const hasSession = sessionStorage.getItem(sessionKey) === "1";

  const returningSameDay = lastDay === today && !hasSession;

  localStorage.setItem(dayKey, today);
  sessionStorage.setItem(sessionKey, "1");

  if (returningSameDay) {
    if (hour < 12) return `Back at it, ${name}`;
    if (hour < 17) return `Welcome back, ${name}`;
    return `Still going, ${name}`;
  }

  if (hour < 5) return `Burning the midnight oil, ${name}?`;
  if (hour < 12) return `Good morning, ${name}`;
  if (hour < 17) return `Good afternoon, ${name}`;
  return `Good evening, ${name}`;
}

function firstNameFromProfile(user: any): string {
  if (user?.firstName && typeof user.firstName === "string") {
    return user.firstName.trim();
  }
  const raw = user?.name || user?.organizerName || user?.username || "";
  if (!raw || typeof raw !== "string") return "";
  return raw.trim().split(/\s+/)[0];
}

/**
 * Ask Ev — Eventox-branded chat. Conversation survives event preview navigation.
 */
const AiAssistantPage: React.FC = () => {
  const { isNightMode } = useNightMode();
  const { user, isAuthLoading } = useAuth();
  // AuthContext.user is null after SPA login until profile fetch — also check localStorage
  const isAskEvEnabled = resolveAskEvAccess(user);
  const hasToken = typeof window !== "undefined" && !!localStorage.getItem("token");
  // Don't flash the locked screen while profile is still loading for a logged-in user
  const accessPending = hasToken && !isAskEvEnabled && isAuthLoading;
  const initial = useMemo(() => readInitialChat(), []);
  const [messages, setMessages] = useState<ChatMessage[]>(initial.messages);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>(
    initial.conversationId
  );
  const [pendingEdit, setPendingEdit] = useState<PendingEditPayload | undefined>(
    initial.pendingEdit
  );
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const didRestoreScroll = useRef(false);

  const displayName = useMemo(() => firstNameFromProfile(user), [user]);
  const greeting = useMemo(() => buildTimedGreeting(displayName || "there"), [displayName]);
  const hasChat = messages.length > 0;
  // Same light-mode hook other v3 pages use (CreateEventV3 etc.)
  const pageThemeClass = isNightMode ? "" : "light-theme";

  // Persist chat so opening an event and coming back does not wipe the thread
  useEffect(() => {
    if (!isAskEvEnabled) return;
    if (messages.length === 0) {
      clearAskEvChat();
      return;
    }
    saveAskEvChat({ messages, conversationId, pendingEdit });
  }, [messages, conversationId, pendingEdit, isAskEvEnabled]);

  useEffect(() => {
    if (!isAskEvEnabled) return;
    if (!didRestoreScroll.current && messages.length > 0) {
      didRestoreScroll.current = true;
      bottomRef.current?.scrollIntoView({ behavior: "auto" });
      return;
    }
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, isAskEvEnabled]);

  // Keep the composer focused so you can keep typing without re-tapping the chat
  const focusComposer = () => {
    const run = () => inputRef.current?.focus({ preventScroll: true });
    // Double rAF: first paint may still be hero→dock swap after the first send
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
  };

  useEffect(() => {
    if (!isAskEvEnabled) return;
    focusComposer();
  }, [hasChat, isAskEvEnabled]);

  // After a reply finishes (or loading starts clearing), put the caret back in the box
  useEffect(() => {
    if (!isAskEvEnabled || loading) return;
    focusComposer();
  }, [loading, isAskEvEnabled]);

  // Wait for auth profile — avoids false “not enabled” right after SPA login
  if (accessPending) {
    return (
      <div className={`v3-wrap px-3 py-4 ai-ev-page is-locked ${pageThemeClass}`}>
        <Background />
        <PageHeader title="Ask Ev" subtitle={ASK_EV_SUBTITLE} icon={Sparkles} isLoading />
        <main className="ai-ev-main">
          <div className="ai-ev-locked">
            <PulseLoader color="#EE3F7C" size={10} />
          </div>
        </main>
      </div>
    );
  }

  // Locked — feature not assigned by Eventox admin
  if (!isAskEvEnabled) {
    return (
      <div className={`v3-wrap px-3 py-4 ai-ev-page is-locked ${pageThemeClass}`}>
        <Background />
        <PageHeader title="Ask Ev" subtitle={ASK_EV_SUBTITLE} icon={Sparkles} />
        <main className="ai-ev-main">
          <div className="glass-panel ai-ev-locked">
            <AskEvMark size="lg" />
            <h1>Ask Ev isn’t enabled</h1>
            <p>
              This feature is available only for organizers approved by Eventox.
              Please contact your administrator to request access.
            </p>
          </div>
        </main>
      </div>
    );
  }

  const startNewChat = () => {
    clearAskEvChat();
    setMessages([]);
    setConversationId(undefined);
    setPendingEdit(undefined);
    setInput("");
    inputRef.current?.focus();
    focusComposer();
  };

  const sendMessage = async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading || editBusy) return;

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: "user",
      content: text,
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);
    // Refocus right away — send button / Enter must not leave the composer unfocused
    focusComposer();

    try {
      const data = await sendAiChat({
        message: text,
        conversationId,
      });
      if (data?.conversationId) {
        setConversationId(data.conversationId);
      }
      // yes/no confirm path clears pending
      if (data?.pendingEdit) {
        setPendingEdit(data.pendingEdit);
      } else if (/^(yes|y|yeah|yep|confirm|ok|okay|no|n|nope|cancel)$/i.test(text.trim())) {
        setPendingEdit(undefined);
      }
      const events = Array.isArray(data?.events) ? data.events : undefined;
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: "assistant",
          content: data?.reply || "No response from Ask Ev.",
          events: events?.length ? events : undefined,
          pendingEdit: data?.pendingEdit || undefined,
        },
      ]);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to reach Ask Ev. Check API and login.";
      toast.error(msg);
      setMessages((prev) => [
        ...prev,
        {
          id: `e-${Date.now()}`,
          role: "assistant",
          content: `Sorry — ${msg}`,
        },
      ]);
    } finally {
      setLoading(false);
      focusComposer();
    }
  };

  const handleConfirmEdit = async () => {
    if (!conversationId || !pendingEdit || editBusy) return;
    setEditBusy(true);
    try {
      const data = await confirmAiEdit({
        conversationId,
        proposalId: pendingEdit.proposalId,
      });
      setPendingEdit(undefined);
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: "assistant",
          content: data?.reply || "Edit confirmed.",
          events: Array.isArray(data?.events) && data.events.length ? data.events : undefined,
        },
      ]);
      toast.success("Event updated");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || "Confirm failed");
    } finally {
      setEditBusy(false);
    }
  };

  const handleCancelEdit = async () => {
    if (!conversationId || !pendingEdit || editBusy) return;
    setEditBusy(true);
    try {
      const data = await cancelAiEdit({
        conversationId,
        proposalId: pendingEdit.proposalId,
      });
      setPendingEdit(undefined);
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: "assistant",
          content: data?.reply || "Edit cancelled.",
        },
      ]);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || "Cancel failed");
    } finally {
      setEditBusy(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void sendMessage();
    }
  };

  const suggestions = [
    "List my events",
    "I want to rename an event",
    "Show my drafts",
  ];

  return (
    <div
      className={`v3-wrap px-3 py-4 ai-ev-page ${pageThemeClass} ${
        hasChat ? "has-chat" : "is-empty"
      }`}
    >
      <Background />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme={isNightMode ? "dark" : "light"}
      />

      <PageHeader
        title="Ask Ev"
        subtitle={ASK_EV_SUBTITLE}
        icon={Sparkles}
        isLoading={loading}
        actions={
          hasChat ? (
            <button
              type="button"
              className="v3-btn-secondary"
              onClick={startNewChat}
              style={{ fontSize: 12 }}
            >
              <Plus size={14} /> New Chat
            </button>
          ) : undefined
        }
      />

      <main className="ai-ev-main">
        {!hasChat ? (
          <div className="ai-ev-hero">
            <h2 className="ai-ev-greeting">
              <AskEvMark size="lg" />
              {greeting}
            </h2>

            <div className="glass-panel ai-ev-composer ai-ev-composer-hero">
              {/* Keep enabled while loading so focus isn't stolen / blocked */}
              <textarea
                ref={inputRef}
                className="v3-input ai-ev-input"
                rows={1}
                placeholder="How can I help you today?"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                autoFocus
              />
              <div className="ai-ev-composer-bar">
                <span className="ai-ev-hint">Enter to send · Shift+Enter for new line</span>
                <button
                  type="button"
                  className="v3-btn-primary ai-ev-send"
                  onClick={() => void sendMessage()}
                  disabled={loading || !input.trim()}
                  title="Send"
                >
                  {loading ? <PulseLoader color="#fff" size={5} /> : <Send size={16} />}
                </button>
              </div>
            </div>

            <div className="ai-ev-suggestions">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="v3-btn-secondary ai-ev-chip"
                  onClick={() => void sendMessage(s)}
                  disabled={loading}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="ai-ev-thread">
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`ai-ev-msg ${m.role === "user" ? "is-user" : "is-assistant"}`}
                >
                  {m.role === "assistant" && <AskEvMark size="sm" />}
                  <div className="ai-ev-msg-body">
                    {m.events && m.events.length > 0 ? (
                      <AiEventList events={m.events} caption={m.content} />
                    ) : (
                      <p className="ai-ev-msg-text">
                        {m.content.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
                          if (part.startsWith("**") && part.endsWith("**")) {
                            return <strong key={i}>{part.slice(2, -2)}</strong>;
                          }
                          return <React.Fragment key={i}>{part}</React.Fragment>;
                        })}
                      </p>
                    )}
                    {m.pendingEdit &&
                      pendingEdit?.proposalId === m.pendingEdit.proposalId && (
                        <AiEditProposal
                          pending={m.pendingEdit}
                          busy={editBusy}
                          onConfirm={() => void handleConfirmEdit()}
                          onCancel={() => void handleCancelEdit()}
                        />
                      )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="ai-ev-msg is-assistant">
                  <AskEvMark size="sm" />
                  <div className="ai-ev-msg-body">
                    <PulseLoader color="#F79B59" size={7} />
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            <div className="glass-panel ai-ev-composer ai-ev-composer-dock">
              {/* Never disable while waiting — that forced re-tapping to type again */}
              <textarea
                ref={inputRef}
                className="v3-input ai-ev-input"
                rows={1}
                placeholder="Reply to Ask Ev…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                autoFocus
              />
              <div className="ai-ev-composer-bar">
                <span className="ai-ev-hint">Enter to send · Shift+Enter for new line</span>
                <button
                  type="button"
                  className="v3-btn-primary ai-ev-send"
                  onClick={() => void sendMessage()}
                  disabled={loading || !input.trim()}
                  title="Send"
                >
                  {loading ? <PulseLoader color="#fff" size={5} /> : <Send size={16} />}
                </button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AiAssistantPage;
